import Immutable from 'immutable';
import { createSelector } from 'reselect';
import _ from 'lodash';
import { getSummary } from './BorrowingSelector';
import { getShelvesForCurrentUser } from './ShelfSelector';
import {
  itemMatchesStatusFilter,
  getMaterialTypeCountsForStatus,
  getPresentStatusesFromSummary,
  getCountsByStatusFromSummary
} from '@bibliocommons/utils-borrowing';

const statusFilter = (state, props) => _.get(props, 'router.params.status');

const holdsById = state => state.getIn(['entities', 'holds']);
const currentHoldIds = state => state.getIn(['borrowing', 'holds', 'items'], Immutable.List());

const bibsById = state => state.getIn(['entities', 'bibs'], Immutable.Map());
const holdsSummary = state => getSummary(state, 'holds');
const inactiveHoldsSummary = state => getSummary(state, 'inactiveHolds');
const holdsTransactions = state => state.getIn(['borrowing', 'holds', 'holdsTransactions'], Immutable.Map());
const holdsStatus = state => state.getIn(['borrowing', 'holds'], Immutable.Map());
const pagination = state => state.getIn(['borrowing', 'holds', 'pagination'], Immutable.Map());

const getQuotas = state => getSummary(state, 'holds').get('quotas', Immutable.Map());

const holdIsActive = (hold) =>
  !['CANCELLED', 'EXPIRED'].includes(hold.get('status'));

export const getActiveHoldsByMetadataId = createSelector(
  [holdsById],
  (allHoldsById) => allHoldsById
    .filter(holdIsActive)
    .groupBy(hold => hold.get('metadataId'))
);

export const getHoldsWithBibsById = createSelector(
  [holdsById, bibsById],
  (holds, bibs) => holds.map(hold => hold && hold.set('bib', bibs.get(hold.get('metadataId'))))
);

export const getActiveHolds = createSelector(
  [holdsById],
  (holds) => holds.filter(hold => holdIsActive(hold))
);

export const getILSQuotas = createSelector(
  [getQuotas],
  (quotas) => (
    quotas
      .valueSeq().toArray()
      .filter(x => x.get('type') === 'ILS')[0]
  )
);

export const getCurrentHolds = createSelector(
  [currentHoldIds, getHoldsWithBibsById, bibsById, getShelvesForCurrentUser],
  (holdIds, holdsWithBibs, bibs, shelfItems) => (
    holdIds
      .map(holdId => holdsWithBibs.get(holdId))
      .filter(hold => !!hold)
      .map((hold) => {
        const isAlreadyOnShelf = shelfItems.some(
          (item) => (item.get('metadataId') === hold.get('metadataId'))
        );

        const canAddToShelf = !isAlreadyOnShelf && hold.get('bib');

        const actions = canAddToShelf ?
          hold.get('actions', Immutable.List()).push('addToShelf') :
          hold.get('actions');

        return hold.set('actions', actions);
      })
      .toList()
  )
);

export const getHoldsUnfilteredByMaterialType = createSelector(
  [holdsById, statusFilter],
  (holds, selectedStatus) =>
    holds
      .filter((hold) => itemMatchesStatusFilter(hold, selectedStatus))
      .toList()
);

export const getSelectedStatus = createSelector(
  [statusFilter],
  (status) => status ? status.toUpperCase() : status
);

export const getMaterialTypeCounts = createSelector(
  [holdsSummary, statusFilter],
  (summary, currentStatus) => getMaterialTypeCountsForStatus(summary, currentStatus)
);

export const getPresentStatuses = createSelector(
  [holdsSummary],
  (summary) => getPresentStatusesFromSummary(summary)
);

export const getHoldsQuotas = createSelector(
  [holdsSummary],
  (summary) => summary.get('quotas', Immutable.List())
);

export const getCountsByStatus = createSelector(
  [holdsSummary],
  (summary) => getCountsByStatusFromSummary(summary)
);

export const getInactiveHoldCountsByStatus = createSelector(
  [inactiveHoldsSummary],
  (summary) => getCountsByStatusFromSummary(summary)
);

export const getDataForStatus = (state, status) => state.getIn(['borrowing', 'holds', status], Immutable.Map());

export const getHoldsForStatus = createSelector(
  [holdsById, bibsById, getDataForStatus],
  (holds, bibs, holdsData) => (
    holdsData.get('holds', Immutable.List())
      .map(id => holds.get(id))
      .filter(hold => hold)
      .map((hold) => hold.set('bib', bibs.get(hold.get('metadataId'))))
  )
);

export const getHoldsTransactions = createSelector(
  [holdsTransactions],
  (transactions) => transactions
);

export const getHoldsStatus = createSelector(
  [holdsStatus],
  (status) => status
);

const inactiveHoldData = state => state.getIn(['borrowing', 'inactiveHolds'], Immutable.Map());

const getInactiveHoldsByStatus = (status) => createSelector(
  [holdsById, inactiveHoldData],
  (holds, inactiveHolds) => inactiveHolds.getIn([status, 'holdIds'], Immutable.List())
    .map(holdId => holds.get(holdId))
);

export const getInactiveHoldData = createSelector(
  [inactiveHoldData],
  (data) => data
);

export const getPagination = createSelector(
  [pagination],
  (page) => page
);

export const getCancelledHolds = getInactiveHoldsByStatus('cancelled');
export const getExpiredHolds = getInactiveHoldsByStatus('expired');

export default {
  getActiveHoldsByMetadataId,
  getHoldsWithBibsById,
  getCurrentHolds,
  getHoldsUnfilteredByMaterialType,
  getSelectedStatus,
  getMaterialTypeCounts,
  getPresentStatuses,
  getHoldsQuotas,
  getCountsByStatus,
  getDataForStatus,
  getHoldsForStatus,
  getActiveHolds,
  getPagination,
  getHoldsTransactions,
  getHoldsStatus,
  getInactiveHoldData,
  getCancelledHolds,
  getExpiredHolds
};



// WEBPACK FOOTER //
// ./app/selectors/HoldsSelector.js